import { StringParam, useParam } from "@redotech/react-router-util/param";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import { useTranslation } from "@redotech/redo-i18n";
import ArrowLeftIcon from "@redotech/redo-icons/arrow-left.svg";
import CheckCircleIcon from "@redotech/redo-icons/check-circle.svg";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { getInventorySourceFilterGroup } from "@redotech/redo-model/fulfillments/inventory-source";
import { LocationId } from "@redotech/redo-model/ids";
import { FilterOperator } from "@redotech/redo-model/views/advanced-filters/generic-advanced-filter-data";
import { FilterGroup } from "@redotech/redo-model/views/views";
import {
  PickSessionStrategy,
  defaultStrategyOrderCount,
} from "@redotech/redo-model/wms/schema/pick-session";
import {
  AdvancedTableFilter,
  decodeAdvancedFilterGroup,
} from "@redotech/redo-web/advanced-filters/advanced-filter";
import { alertOnFailure } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { EnabledFulfillmentProvidersContext } from "../../common/enabled-fulfillment-providers";
import { SuppressPrintNotificationsProvider } from "../../common/print-queue-context";
import { getFGAdvancedFilters } from "../../fulfillment-groups/filters/get-fg-advanced-filters";
import { OriginLocationFilter } from "../../fulfillment-groups/filters/origin-location-filter";
import { PickSessionExistsFilter } from "../../fulfillment-groups/filters/pick-session-exists-filter";
import { RedoOutboundLabelsRpcClientContext } from "../../outbound-labels-rpc-client";
import { getTeamOMSUsers } from "../../utils/get-team-oms-users";
import { QuantityIncrementDecrement } from "../shared/quantity-increment-decrement";
import * as styles from "./create-pick-session-page.module.css";
import { PickSessionMetrics } from "./pick-session-metrics";

export const CreatePickSessionPage = memo(function CreatePickSessionPage() {
  const { t } = useTranslation();
  const [locationId] = useParam(new StringParam("locationId", ""));
  const [strategy] = useParam(new StringParam("strategy", ""));
  const [filterName] = useParam(new StringParam("filterName", ""));
  const rpcClient = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const rpcMerchantClient = useRequiredContext(RedoMerchantRpcClientContext);
  const team = useRequiredContext(TeamContext);
  const teamUsers = getTeamOMSUsers(team);
  const enabledProviders = useRequiredContext(
    EnabledFulfillmentProvidersContext,
  );
  const navigate = useNavigate();
  const screenType = useScreenType();

  const [orderQuantity, setOrderQuantity] = useState<number>(() => {
    const validStrategy = Object.values(PickSessionStrategy).includes(
      strategy as PickSessionStrategy,
    )
      ? (strategy as PickSessionStrategy)
      : PickSessionStrategy.MULTI_ITEM;

    return (
      team?.settings?.outboundLabels?.wms?.pickSession?.defaultOrderCount?.[
        validStrategy
      ] ||
      defaultStrategyOrderCount[validStrategy] ||
      8
    );
  });

  const advancedFilters = useMemo(
    (): AdvancedTableFilter[] => {
      const tableFilters = getFGAdvancedFilters({
        team,
        users: teamUsers,
        rpcClient,
        rpcMerchantClient,
        enabledProviders,
      });

      return [...tableFilters, PickSessionExistsFilter, OriginLocationFilter];
    },
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [filterGroupFromParams] = useParam(new StringParam("filterGroup", ""));

  const appliedFilterGroup = useMemo((): FilterGroup | null => {
    if (!filterGroupFromParams) {
      return null;
    }
    return decodeAdvancedFilterGroup(filterGroupFromParams, advancedFilters);
  }, [filterGroupFromParams, advancedFilters]);

  const effectiveFilterGroup = useMemo((): FilterGroup | null => {
    if (!appliedFilterGroup) {
      return null;
    }
    return {
      operator: FilterOperator.AND,
      clauses: [getInventorySourceFilterGroup(undefined), appliedFilterGroup],
    };
  }, [appliedFilterGroup]);

  const availableOrders = useLoad(async () => {
    if (!effectiveFilterGroup) {
      setOrderQuantity(0);
      return 0;
    }

    const { total } = await rpcClient.getFulfillmentGroupSummaryCount({
      advancedFiltersData: [],
      filterGroup: effectiveFilterGroup,
      merchantTimezone: team.ianaTimezone,
    });

    const validStrategy = Object.values(PickSessionStrategy).includes(
      strategy as PickSessionStrategy,
    )
      ? (strategy as PickSessionStrategy)
      : PickSessionStrategy.MULTI_ITEM;

    const defaultOrderCount =
      team?.settings?.outboundLabels?.wms?.pickSession?.defaultOrderCount?.[
        validStrategy
      ] ||
      defaultStrategyOrderCount[validStrategy] ||
      8;

    if (total < defaultOrderCount) {
      setOrderQuantity(total);
    }

    return total;
  }, [team, rpcClient, effectiveFilterGroup]);

  const [created, setCreated] = useState(false);
  const [rejectedGroups, setRejectedGroups] = useState<
    | {
        orderName: string;
        reason: string;
        productSku?: string;
        productVariantTitle?: string;
        productImageUrl?: string;
      }[]
    | undefined
  >(undefined);

  const [createLoad, create] = useTriggerLoad((signal) =>
    alertOnFailure("Pick session creation failed")(async () => {
      const validatedStrategy = Object.values(PickSessionStrategy).includes(
        strategy as PickSessionStrategy,
      )
        ? (strategy as PickSessionStrategy)
        : PickSessionStrategy.SINGLE_ITEM;

      const useIncremental =
        localStorage.getItem("useIncrementalPickSession") === "true";
      const useBatchCommit = localStorage.getItem("useBatchCommit") === "true";

      const result = await rpcClient.createPickSession(
        {
          numberOfGroups: orderQuantity,
          searchInput: {
            advancedFiltersData: [],
            filterGroup: effectiveFilterGroup ?? undefined,
            merchantTimezone: team.ianaTimezone,
          },
          seedGroupId: null,
          locationId: locationId as LocationId,
          strategy: validatedStrategy,
          filterName: filterName || null,
          useIncremental,
          useBatchCommit,
        },
        { signal },
      );

      // pickSessionId wins over rejections — the server now returns both on
      // partial success. This flow's UX has always been "navigate to the
      // new session on success," so we preserve that and only show the
      // rejection screen when no session was created at all.
      if (result.pickSessionId) {
        setCreated(true);
        navigate(`../pick-sessions/${result.pickSessionId}`);
      } else if (result.rejectedGroups) {
        setRejectedGroups(
          result.rejectedGroups.map((g) => ({
            orderName: g.orderNames.join(", ") || g.groupId,
            reason: g.reason,
            productSku: g.productSku,
            productVariantTitle: g.productVariantTitle,
            productImageUrl: g.productImageUrl,
          })),
        );
      }
    }),
  );

  if (!locationId || !strategy) {
    return (
      <SuppressPrintNotificationsProvider>
        <Flex align="center" dir="column" gap="xl" p="3xl" w="full">
          <Text fontSize="lg" fontWeight="semibold" textColor="primary">
            {t("Location required")}
          </Text>
          <Text fontSize="md" textAlign="center" textColor="secondary">
            Missing required information, locationId or strategy.
          </Text>
          <RedoButton
            hierarchy="primary"
            onClick={() => navigate(-1)}
            size="lg"
            text={t("Go Back")}
            theme="normal"
          />
        </Flex>
      </SuppressPrintNotificationsProvider>
    );
  }

  return (
    <SuppressPrintNotificationsProvider>
      <Flex dir="column" gap="3xl" p="3xl" w="full">
        <Flex justify="flex-start" w="full">
          <RedoButton
            hierarchy={
              screenType === ScreenType.MOBILE ? "tertiary" : "secondary"
            }
            IconLeading={ArrowLeftIcon}
            onClick={() =>
              navigate(`../pick-sessions?locationId=${locationId}`)
            }
            size={screenType === ScreenType.MOBILE ? "xs" : "lg"}
            text={t("Back to Dashboard")}
            theme="normal"
          />
        </Flex>

        {screenType !== ScreenType.MOBILE && (
          <PickSessionMetrics locationId={locationId || null} />
        )}

        {rejectedGroups ? (
          <Card p="3xl" radius="md" w="full">
            <Flex dir="column" gap="xl">
              <Flex dir="column" gap="xs">
                <Text fontSize="lg" fontWeight="semibold" textAlign="center">
                  {t("No orders could be picked")}
                </Text>
                <Text fontSize="sm" textAlign="center" textColor="secondary">
                  {t(
                    "{{count}} order(s) were checked but none could be added to this pick session.",
                    { count: rejectedGroups.length },
                  )}
                </Text>
              </Flex>
              <Flex dir="column" gap="none">
                {rejectedGroups.map((group, index) => (
                  <Flex
                    dir="column"
                    gap="xs"
                    key={group.orderName + index}
                    style={{
                      borderBottom:
                        index < rejectedGroups.length - 1
                          ? "1px solid var(--redo-colors-border-border-secondary)"
                          : undefined,
                      paddingBottom:
                        index < rejectedGroups.length - 1
                          ? "var(--redo-spacing-md)"
                          : undefined,
                      paddingTop:
                        index > 0 ? "var(--redo-spacing-md)" : undefined,
                    }}
                  >
                    <Text fontSize="sm" fontWeight="semibold">
                      {group.orderName}
                    </Text>
                    {(group.productImageUrl ||
                      group.productVariantTitle ||
                      group.productSku) && (
                      <Flex align="center" gap="sm">
                        {group.productImageUrl && (
                          <img
                            alt={
                              group.productVariantTitle ??
                              group.productSku ??
                              "product"
                            }
                            src={group.productImageUrl}
                            style={{
                              width: 36,
                              height: 36,
                              objectFit: "cover",
                              borderRadius: 4,
                              flexShrink: 0,
                            }}
                          />
                        )}
                        <Flex dir="column" gap="none">
                          {group.productVariantTitle && (
                            <Text fontSize="xs">
                              {group.productVariantTitle}
                            </Text>
                          )}
                          {group.productSku && (
                            <Text fontSize="xs" textColor="tertiary">
                              {group.productSku}
                            </Text>
                          )}
                        </Flex>
                      </Flex>
                    )}
                    <Text fontSize="xs" textColor="secondary">
                      {group.reason}
                    </Text>
                  </Flex>
                ))}
              </Flex>
              <RedoButton
                hierarchy="secondary"
                onClick={() => setRejectedGroups(undefined)}
                size="lg"
                text={t("Try again")}
                theme="normal"
              />
            </Flex>
          </Card>
        ) : (
          <Card p="3xl" radius="md" w="full">
            <Flex align="center" dir="column" justify="center">
              <Text fontSize="lg" fontWeight="semibold" textAlign="center">
                {t("How many orders do you want to fulfill?")}
              </Text>

              <QuantityIncrementDecrement
                max={Math.min(99, availableOrders?.value || 99)}
                min={0}
                setValue={setOrderQuantity}
                title={t("Out of {{count}} orders", {
                  count: availableOrders?.value || 0,
                })}
                value={orderQuantity}
              />

              <div style={{ width: "100%" }}>
                <RedoButton
                  buttonClassName={styles.startPickingButton}
                  disabled={
                    !availableOrders?.value || orderQuantity < 1 || created
                  }
                  hierarchy="primary"
                  IconLeading={CheckCircleIcon}
                  onClick={() => {
                    create();
                  }}
                  pending={createLoad.pending || created}
                  size="xl"
                  text={t("Start pick session")}
                  theme="normal"
                />
              </div>
            </Flex>
          </Card>
        )}
      </Flex>
    </SuppressPrintNotificationsProvider>
  );
});
